import { useState, useEffect } from 'react';
import { getLeaderboard, getStaff, createStaff, updateStaff, deleteStaff } from '../utils/api';
import PeriodPicker from '../components/PeriodPicker';
import { formatCurrency } from '../utils/format';
import toast from 'react-hot-toast';
import { hasRole } from '../utils/people';

/* Leaderboard — active staff only, ranked by revenue (a rep with fewer but bigger jobs ranks higher).
   Jobs sold, revenue and average per job for the chosen month / quarter / year, straight from the
   records: sold date in the period, cancelled jobs left out, revenue = Change Order total or Contract
   Amount, shared jobs credited by split %. Inactive staff are hidden here; their sales stay in the
   records and still show on the Sales Report. Manage Staff lists everyone, active or not.         */
const ROLES = ['Sales Rep', 'Telemarketer', 'Canvasser', 'Production'];
const emptyForm = { name: '', role: 'Sales Rep', status: 'Active' };

const REPORT_GROUPS = [
  { key: 'salesReps',     title: 'Sales Reps',     one: 'sales rep',    ic: '💼', hc: 'rh-sales', accent: '#2d6e35' },
  { key: 'telemarketers', title: 'Telemarketers',  one: 'telemarketer', ic: '📞', hc: 'rh-tele',  accent: '#1976d2' },
  { key: 'canvassers',    title: 'Canvassers',     one: 'canvasser',    ic: '🚪', hc: 'rh-canv',  accent: '#8e24aa' },
  { key: 'confirmers',    title: 'Confirmers',     one: 'confirmer',    ic: '🎧', hc: 'rh-conf',  accent: '#ad1457' },
];
const MEDALS = ['🥇', '🥈', '🥉'];
const RANKBG = ['#d4a017', '#9aa3a0', '#cd7f32'];
const AV = ['#2d6e35', '#1976d2', '#8e24aa', '#d4a017', '#e0533d', '#0e9488', '#7c3aed', '#c2410c'];

const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
function avColor(name) { let h = 0; for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) % AV.length; return AV[h]; }

function useCountUp(target, dur = 1000) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, start; const to = Number(target) || 0;
    const tick = (t) => { if (!start) start = t; const p = Math.min((t - start) / dur, 1); setV(to * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
}

const PrintIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 15, height: 15 }}>
    <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9h8v4H6v-4zm-2-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd"/>
  </svg>
);

const SR_CSS = `
.sr-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:26px}
.sr-tile{position:relative;border-radius:18px;padding:18px;color:#fff;overflow:hidden;box-shadow:0 10px 24px rgba(0,0,0,.12);min-height:104px;animation:srFade .5s both}
.sr-tile .ic{font-size:1.4rem}
.sr-tile .big{font-family:var(--font-display,inherit);font-size:2rem;font-weight:800;line-height:1;margin-top:8px}
.sr-tile.mvp .big{font-size:1.35rem;margin-top:10px}
.sr-tile .lbl{font-size:.72rem;letter-spacing:.04em;text-transform:uppercase;opacity:.92;margin-top:5px;font-weight:600}
.sr-tile .spark{position:absolute;right:-12px;top:-12px;width:84px;height:84px;border-radius:50%;background:rgba(255,255,255,.14)}
.sr-t-jobs{background:linear-gradient(135deg,#34c759,#1a8a44)}
.sr-t-rev{background:linear-gradient(135deg,#e6b422,#b8860b)}
.sr-t-staff{background:linear-gradient(135deg,#3b82f6,#1d4ed8)}
.sr-t-mvp{background:linear-gradient(135deg,#a855f7,#6a1b9a)}
.sr-role{background:#fff;border-radius:18px;box-shadow:0 6px 18px rgba(0,0,0,.06);margin-bottom:24px;overflow:hidden;border:1px solid #eef0ee}
.sr-role-head{display:flex;align-items:center;gap:12px;padding:14px 20px;color:#fff}
.sr-role-head .r-ic{font-size:1.4rem}
.sr-role-head h3{font-family:var(--font-display,inherit);font-weight:800;font-size:1.12rem;flex:1;margin:0;color:#fff}
.sr-role-head .r-tot{display:flex;gap:16px;font-size:.8rem;font-weight:600;opacity:.96}
.sr-role-head .r-tot b{font-size:.98rem}
.rh-sales{background:linear-gradient(120deg,#2d6e35,#1a4520)}
.rh-tele{background:linear-gradient(120deg,#1976d2,#0d47a1)}
.rh-canv{background:linear-gradient(120deg,#8e24aa,#4A148C)}
.rh-conf{background:linear-gradient(120deg,#ad1457,#6a0f36)}
.sr-photo{object-fit:cover}
.sr-nick{display:block;font-size:.74rem;font-weight:800;color:#C2410C;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sr-podium{display:flex;justify-content:center;align-items:flex-end;gap:14px;padding:30px 20px 18px;background:radial-gradient(120% 90% at 50% 0%,#fafdfb,#f2f6f3)}
.sr-pcard{width:31%;max-width:215px;background:#fff;border-radius:16px;padding:16px 14px 14px;text-align:center;position:relative;box-shadow:0 8px 20px rgba(0,0,0,.1);border:1px solid #eee;animation:srRise .6s both}
.sr-pcard.p1{transform:translateY(-16px);border:2px solid #f0c842;box-shadow:0 14px 30px rgba(212,160,23,.28);animation-delay:.05s}
.sr-pcard.p2{animation-delay:.18s}.sr-pcard.p3{animation-delay:.3s}
.sr-pcard .crown{position:absolute;top:-25px;left:50%;transform:translateX(-50%);font-size:1.6rem}
.sr-medal{font-size:1.6rem;line-height:1}
.sr-prank{font-size:.68rem;font-weight:800;letter-spacing:.06em;color:#fff;border-radius:20px;padding:2px 10px;display:inline-block;margin-bottom:4px}
.sr-avatar{width:54px;height:54px;border-radius:50%;margin:6px auto 8px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-family:var(--font-display,inherit);font-size:1.1rem;box-shadow:0 4px 10px rgba(0,0,0,.18)}
.sr-pcard.p1 .sr-avatar{width:66px;height:66px;font-size:1.35rem}
.sr-pname{font-weight:700;font-size:.92rem;color:var(--gray-700);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sr-pjobs{font-family:var(--font-display,inherit);font-weight:800;font-size:1.45rem;margin-top:4px}
.sr-pjobs small{font-size:.68rem;color:#9aa3a0;font-weight:600}
.sr-pval{font-size:.78rem;color:var(--gray-500);font-weight:600}
.sr-board{padding:6px 16px 14px}
.sr-lb{display:grid;grid-template-columns:30px 40px 1fr 120px 104px 64px 96px;align-items:center;gap:12px;padding:9px 6px;border-radius:12px}
.sr-lb.head{font-size:.68rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#9aa3a0;padding-top:12px}
.sr-lb.head:hover{background:none}
.sr-pstats{display:flex;justify-content:center;gap:10px;margin-top:6px;font-size:.74rem;color:#7a8a82;font-weight:600}
.sr-pstats b{color:var(--gray-700);font-size:.86rem}
.sr-avg{text-align:right;font-weight:600;color:#7a8a82;font-size:.82rem}
.sr-period{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;margin-bottom:18px}
.sr-period .note{font-size:.78rem;color:#7a8a82}
.sr-zero{opacity:.55}
.sr-recon{border-top:1px dashed #dfe5e1;margin:4px 16px 0;padding:12px 6px 16px;font-size:.84rem;color:#5a685f}
.sr-recon .sum{font-weight:700;color:#33463E;margin-bottom:6px}
.sr-recon ul{margin:4px 0 0;padding-left:18px}
.sr-recon li{margin:2px 0}
.sr-recon .warn{color:#B35C00}
.sr-lb+.sr-lb{border-top:1px solid #f1f3f1}
.sr-lb:hover{background:var(--green-50)}
.sr-rank{font-weight:800;color:#9aa3a0;text-align:center;font-family:var(--font-display,inherit)}
.sr-av{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:.82rem}
.sr-nm{font-weight:700;color:var(--gray-700)}
.sr-nm small{display:block;font-weight:600;font-size:.72rem;margin-top:1px}
.sr-bar{height:12px;border-radius:8px;background:#eef1ee;overflow:hidden}
.sr-bar i{display:block;height:100%;border-radius:8px;width:0;transition:width 1.1s cubic-bezier(.2,.8,.2,1)}
.sr-jobs{text-align:center;font-weight:800;font-family:var(--font-display,inherit)}
.sr-val{text-align:right;font-weight:700;color:var(--gray-700);font-size:.88rem}
.sr-dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:5px;vertical-align:middle}
@keyframes srRise{from{opacity:0;transform:translateY(18px)}}
@keyframes srFade{from{opacity:0;transform:translateY(10px)}}
@media(max-width:820px){.sr-tiles{grid-template-columns:repeat(2,1fr)}.sr-lb{grid-template-columns:26px 36px 1fr 92px 48px}.sr-barcell,.sr-avg{display:none}}
`;

function SummaryTiles({ report }) {
  // someone with several roles is on several boards — count each person once
  const all = [...new Map([...(report.salesReps || []), ...(report.telemarketers || []), ...(report.canvassers || []), ...(report.confirmers || [])].map(r => [r.name.toLowerCase(), r])).values()];
  const reps = report.salesReps || [];
  const mvp = reps[0] && reps[0].revenue > 0 ? reps[0] : null;
  const cj = useCountUp(report.company?.jobs || 0), cv = useCountUp(report.company?.revenue || 0), cs = useCountUp(all.length);
  return (
    <div className="sr-tiles">
      <div className="sr-tile sr-t-rev"><div className="spark" /><div className="ic">💰</div><div className="big">{formatCurrency(Math.round(cv))}</div><div className="lbl">Total Revenue</div></div>
      <div className="sr-tile sr-t-jobs"><div className="spark" /><div className="ic">🏆</div><div className="big">{Math.round(cj).toLocaleString()}</div><div className="lbl">Jobs Sold</div></div>
      <div className="sr-tile sr-t-staff"><div className="spark" /><div className="ic">👥</div><div className="big">{Math.round(cs)}</div><div className="lbl">Active Staff</div></div>
      <div className="sr-tile sr-t-mvp mvp"><div className="spark" /><div className="ic">⭐</div><div className="big">{mvp ? mvp.name : '—'}</div><div className="lbl">{mvp ? `Top rep · ${formatCurrency(mvp.revenue)}` : 'No sales yet'}</div></div>
    </div>
  );
}

function RoleBoard({ meta, rows, nl, company }) {
  const [go, setGo] = useState(false);
  useEffect(() => { const t = setTimeout(() => setGo(true), 200); return () => clearTimeout(t); }, []);
  const tj = rows.reduce((s, r) => s + (r.jobs || 0), 0);
  const tv = rows.reduce((s, r) => s + (r.revenue || 0), 0);
  const maxRev = Math.max(...rows.map(r => r.revenue || 0), 1);
  const top3 = rows.filter(r => r.revenue > 0 || r.jobs > 0).slice(0, 3);
  const rest = rows.slice(top3.length);
  const order = [1, 0, 2]; // render 2nd, 1st, 3rd for podium feel

  return (
    <div className="sr-role">
      <div className={`sr-role-head ${meta.hc}`}>
        <div className="r-ic">{meta.ic}</div>
        <h3>{meta.title}</h3>
        <div className="r-tot"><span><b>{formatCurrency(tv)}</b></span><span><b>{tj}</b> jobs</span><span><b>{rows.length}</b> active</span></div>
      </div>

      {top3.length > 0 && (
        <div className="sr-podium">
          {order.filter(i => top3[i]).map(i => {
            const r = top3[i];
            return (
              <div key={r.name} className={`sr-pcard p${i + 1}`}>
                {i === 0 && <div className="crown">👑</div>}
                <div className="sr-prank" style={{ background: RANKBG[i] }}>#{i + 1}</div>
                <div className="sr-medal">{MEDALS[i]}</div>
                {r.photo ? <img className="sr-avatar sr-photo" src={r.photo} alt={r.name} />
                  : <div className="sr-avatar" style={{ background: `linear-gradient(135deg,${avColor(r.name)},${avColor(r.name)}cc)` }}>{initials(r.name)}</div>}
                <div className="sr-pname">{r.name}</div>
                {r.nickname && <div className="sr-nick">“{r.nickname}”</div>}
                <div className="sr-pjobs" style={{ color: meta.accent }}>{formatCurrency(r.revenue || 0)}</div>
                <div className="sr-pstats"><span><b>{r.jobs || 0}</b> job{r.jobs === 1 ? '' : 's'}</span><span>avg <b>{formatCurrency(r.avg || 0)}</b></span></div>
              </div>
            );
          })}
        </div>
      )}

      {rest.length > 0 && (
        <div className="sr-board">
          <div className="sr-lb head"><div>#</div><div /><div>Name</div><div className="sr-barcell" /><div style={{ textAlign: 'right' }}>Revenue</div><div style={{ textAlign: 'center' }}>Jobs</div><div className="sr-avg" title="Average full price of the jobs they were on">Avg job</div></div>
          {rest.map((r, idx) => {
            const pct = Math.round((r.revenue || 0) / maxRev * 100);
            return (
              <div key={r.name} className={'sr-lb' + (r.jobs ? '' : ' sr-zero')}>
                <div className="sr-rank">{idx + top3.length + 1}</div>
                {r.photo ? <img className="sr-av sr-photo" src={r.photo} alt={r.name} />
                  : <div className="sr-av" style={{ background: `linear-gradient(135deg,${avColor(r.name)},${avColor(r.name)}cc)` }}>{initials(r.name)}</div>}
                <div className="sr-nm">{r.name}{r.nickname && <span style={{ color: '#C2410C' }}> · {r.nickname}</span>}{r.shared > 0 && <small style={{ color: '#9aa3a0' }}>{r.shared} shared job{r.shared === 1 ? '' : 's'}, revenue split</small>}</div>
                <div className="sr-barcell"><div className="sr-bar"><i style={{ width: go ? pct + '%' : 0, background: `linear-gradient(90deg,${meta.accent},${meta.accent}aa)` }} /></div></div>
                <div className="sr-val">{formatCurrency(r.revenue || 0)}</div>
                <div className="sr-jobs" style={{ color: meta.accent }}>{r.jobs || 0}</div>
                <div className="sr-avg">{r.jobs ? formatCurrency(r.avg) : '—'}</div>
              </div>
            );
          })}
        </div>
      )}

      {nl && (nl.people.length > 0 || nl.noOne.jobs > 0) && (
        <div className="sr-recon">
          <div className="sum">
            On this board {formatCurrency(tv)}
            {nl.people.length > 0 && <> + not on the board {formatCurrency(nl.people.reduce((s, p) => s + p.revenue, 0))}</>}
            {nl.noOne.jobs > 0 && <> + no {meta.one} entered {formatCurrency(nl.noOne.revenue)}</>}
            {' '}= {formatCurrency(tv + nl.people.reduce((s, p) => s + p.revenue, 0) + nl.noOne.revenue)}{company ? ` of ${formatCurrency(company.revenue)} sold` : ''}
          </div>
          <ul>
            {nl.people.map(p => (
              <li key={p.name}><b>{p.name}</b>: {formatCurrency(p.revenue)} on {p.jobs} job{p.jobs === 1 ? '' : 's'} · <span className="warn">{p.why}</span></li>
            ))}
            {nl.noOne.jobs > 0 && <li>{nl.noOne.jobs} job{nl.noOne.jobs === 1 ? '' : 's'} with no {meta.one} entered: {formatCurrency(nl.noOne.revenue)}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function StaffReport() {
  const [report, setReport] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(null);   // { from, to, label } from the Month / Quarter / Year picker
  const [activeTab, setActiveTab] = useState('report');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { getStaff().then(setStaffList).catch(() => {}); }, []);
  const reload = () => period && getLeaderboard(period.from, period.to).then(setReport).catch(() => toast.error('Could not load the leaderboard'));
  useEffect(() => {
    if (!period) return;
    setLoading(true);
    getLeaderboard(period.from, period.to).then(setReport).catch(() => toast.error('Could not load the leaderboard')).finally(() => setLoading(false));
  }, [period]);

  function openAdd() { setEditing(null); setForm(emptyForm); setShowModal(true); }
  function openEdit(s) { setEditing(s); setForm({ ...s }); setShowModal(true); }

  async function handleSave() {
    if (!form.name?.trim()) return toast.error('Name required');
    try {
      if (editing) {
        const updated = await updateStaff(editing.id, form);
        setStaffList(l => l.map(s => s.id === editing.id ? updated : s));
        toast.success('Staff updated');
      } else {
        const created = await createStaff(form);
        setStaffList(l => [...l, created]);
        toast.success('Staff added');
      }
      setShowModal(false);
      reload();
    } catch { toast.error('Error saving'); }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete ${name}?`)) return;
    await deleteStaff(id);
    setStaffList(l => l.filter(s => s.id !== id));
    reload();
    toast.success('Deleted');
  }

  async function toggleStatus(s) {
    const updated = await updateStaff(s.id, { ...s, status: s.status === 'Active' ? 'Inactive' : 'Active' });
    setStaffList(l => l.map(x => x.id === s.id ? updated : x));
    reload();
  }

  const byRole = ROLES.reduce((acc, r) => ({ ...acc, [r]: staffList.filter(s => hasRole(s, r)) }), {});

  return (
    <>
      <div className="page-header">
        <h2>Leaderboard</h2>
        <div className="page-header-actions">
          {activeTab === 'report' && (
            <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
              <PrintIcon /> Print
            </button>
          )}
          {activeTab === 'manage' && (
            <button className="btn btn-primary" onClick={openAdd}>+ Add Staff</button>
          )}
        </div>
      </div>

      <div className="page-body">
        <div className="tabs">
          <button className={`tab${activeTab === 'report' ? ' active' : ''}`} onClick={() => setActiveTab('report')}>
            Leaderboard
          </button>
          <button className={`tab${activeTab === 'manage' ? ' active' : ''}`} onClick={() => setActiveTab('manage')}>
            Manage Staff
          </button>
        </div>

        {activeTab === 'report' && (
          <div className="sr-period screen-only">
            <PeriodPicker onChange={setPeriod} />
            <span className="note">Ranked by revenue · active staff only · cancelled jobs not counted</span>
          </div>
        )}
        {loading && activeTab === 'report' ? (
          <div className="loader"><div className="spinner" /></div>
        ) : (
          <>
            {/* ── PERFORMANCE REPORT TAB ── */}
            {activeTab === 'report' && report && (
              <>
                {/* Screen view — colorful leaderboard */}
                <div className="screen-only">
                  <style>{SR_CSS}</style>
                  <SummaryTiles report={report} />
                  {REPORT_GROUPS.map(g => {
                    const rows = report[g.key] || [];   // already ranked by revenue on the server
                    return rows.length ? <RoleBoard key={g.key + period?.from} meta={g} rows={rows} nl={report.notListed?.[g.key]} company={report.company} /> : null;
                  })}
                </div>

                {/* Print view (unchanged, B&W friendly) */}
                <div className="print-only">
                  <div style={{ marginBottom: 8, borderBottom: '2px solid #0D3B2E', paddingBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <h2 style={{ fontFamily: 'Georgia,serif', color: '#0D3B2E', fontSize: '14px', margin: 0 }}>CPR4homes — Leaderboard · {period?.label}</h2>
                    <p style={{ fontSize: '8px', color: '#555', margin: 0 }}>Printed: {new Date().toLocaleDateString()}</p>
                  </div>
                  {[
                    { title: 'Sales Reps', data: report.salesReps, color: '#1B5E20' },
                    { title: 'Telemarketers', data: report.telemarketers, color: '#1565C0' },
                    { title: 'Canvassers', data: report.canvassers, color: '#4A148C' },
                    { title: 'Confirmers', data: report.confirmers, color: '#6a0f36' },
                  ].filter(g => g.data && g.data.length).map(({ title, data, color }) => (
                    <div key={title} style={{ marginBottom: 16 }}>
                      <div style={{ background: color, color: '#fff', padding: '4px 8px', fontSize: '10px', fontWeight: 700, WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                        {title} — {formatCurrency(data.reduce((s, r) => s + (r.revenue || 0), 0))} / {data.reduce((s, r) => s + (r.jobs || 0), 0)} jobs
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8px' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5' }}>
                            <th style={{ padding: '3px 6px', textAlign: 'left', borderBottom: '1px solid #ccc' }}>Name</th>
                            <th style={{ padding: '3px 6px', textAlign: 'center', borderBottom: '1px solid #ccc' }}>#</th>
                            <th style={{ padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Revenue</th>
                            <th style={{ padding: '3px 6px', textAlign: 'center', borderBottom: '1px solid #ccc' }}># Jobs</th>
                            <th style={{ padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Avg / Job</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((rep, idx) => (
                            <tr key={rep.name} style={{ background: idx % 2 === 0 ? '#f9f9f9' : '#fff', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                              <td style={{ padding: '3px 6px', borderBottom: '1px solid #eee', fontWeight: 600 }}>{rep.name}{rep.nickname ? ` · ${rep.nickname}` : ''}</td>
                              <td style={{ padding: '3px 6px', borderBottom: '1px solid #eee', textAlign: 'center' }}>{idx + 1}</td>
                              <td style={{ padding: '3px 6px', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(rep.revenue || 0)}</td>
                              <td style={{ padding: '3px 6px', borderBottom: '1px solid #eee', textAlign: 'center' }}>{rep.jobs || 0}</td>
                              <td style={{ padding: '3px 6px', borderBottom: '1px solid #eee', textAlign: 'right' }}>{rep.jobs ? formatCurrency(rep.avg) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── MANAGE STAFF TAB ── */}
            {activeTab === 'manage' && (
              <>
                {ROLES.map(role => byRole[role].length === 0 ? null : (
                  <div key={role} className="card" style={{ marginBottom: 20 }}>
                    <div className="card-header">
                      <h3>{role}s</h3>
                      <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{byRole[role].length} members</span>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr><th>Name</th><th>Role</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                          {byRole[role].map(s => (
                            <tr key={s.id}>
                              <td className="td-name">{s.name}</td>
                              <td>{s.role}</td>
                              <td>
                                <span
                                  className={s.status === 'Active' ? 'badge badge-active' : 'badge badge-cancelled'}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => toggleStatus(s)}
                                  title="Click to toggle status"
                                >
                                  {s.status}
                                </span>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-500)' }} onClick={() => handleDelete(s.id, s.name)}>Delete</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Staff' : 'Add Staff'}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group form-full">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={form.name || ''} onChange={e => setForm(x => ({ ...x, name: e.target.value }))} autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={form.role} onChange={e => setForm(x => ({ ...x, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e => setForm(x => ({ ...x, status: e.target.value }))}>
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
